import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BackendService } from './general/backend.service';
import {
  ClinicalOnboardingConfig,
  ClinicianActivityEvent,
  ClinicianCandidateDocument,
  ClinicianDashboardResponse,
  ClinicianDetailResponse,
  ClinicianDocumentTemplate,
  ClinicianNote,
} from '../models/clinical-onboarding.model';

/**
 * Client per gli endpoint dell'onboarding clinico / lettere di incarico (DBEK-391 epic),
 * esposti da ClinicalOnboardingController.php sotto surveys/{survey_id}/clinical-onboarding.
 * Stesso stile a callback (success_callback/error_callback + backend.showErrors) gia'
 * usato da SurveyService, non un nuovo pattern.
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicalOnboardingService {
  constructor(private backend: BackendService) {}

  /**
   * DBEK-405: il clinico (candidato) legge il proprio stato di impegno per
   * popolare il wizard. Rotta DECOUPLATA da survey_id (candidateId, non
   * surveyId) - stesso motivo di uploadCandidateDocumentReturn sotto.
   */
  public getMyEngagement(
    candidateId: string | number,
    success_callback: (data: {
      engagement: any;
      documents: ClinicianCandidateDocument[];
      survey: { id: number; is_draft: boolean };
      imported: boolean;
    }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`clinical-onboarding/candidates/${candidateId}/my-engagement`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** DBEK-405: il clinico inserisce/aggiorna datore di lavoro, IBAN, codice fiscale, regime fiscale. */
  public updateMyEngagement(
    candidateId: string | number,
    payload: {
      title?: string;
      employer_name?: string;
      employer_type: string;
      iban?: string;
      tax_code?: string;
      fiscal_regime?: string;
    },
    success_callback: (data: any) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`clinical-onboarding/candidates/${candidateId}/my-engagement`, payload).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Ridisegno "documenti flessibili" (sostituisce uploadClearance): il clinico
   * restituisce un file su un documento qualsiasi che lo richiede, non piu' solo sul
   * Nulla Osta hardcoded. Ri-caricabile liberamente finche' non e' VERIFIED.
   */
  public uploadCandidateDocumentReturn(
    candidateId: string | number,
    candidateDocumentId: number,
    file: File,
    success_callback: (data: ClinicianCandidateDocument) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const formData = new FormData();
    formData.append('file', file);

    this.backend
      .postFormData(`clinical-onboarding/candidates/${candidateId}/candidate-documents/${candidateDocumentId}/return`, formData)
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /**
   * Presa visione (piano §3): "la presa visione di un file avviene una volta che si
   * clicca sul file [...] usa quella [API di visualizzazione] per fare il check di
   * presa visione". Stesso pattern gia' in uso in FileViewerService.getFileStream
   * (richiesta autenticata via backend.getBlob, cosi' l'header Authorization arriva
   * al backend - un <a href> diretto non lo farebbe) - chi chiama deve aprire il blob
   * risultante (URL.createObjectURL + window.open), non e' fatto qui per lasciare al
   * componente chiamante la scelta di come mostrarlo.
   */
  public viewCandidateDocument(
    candidateId: string | number,
    candidateDocumentId: number,
    which: 'source' | 'return',
    success_callback: (result: any) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const params = new HttpParams().set('which', which);
    this.backend
      .getBlob(`clinical-onboarding/candidates/${candidateId}/candidate-documents/${candidateDocumentId}/file`, params)
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /**
   * Convenienza usata sia dal wizard del clinico sia dal pannello di dettaglio del
   * gestore: scarica il blob via viewCandidateDocument() (che registra la presa
   * visione lato server quando e' il candidato a guardare il proprio file) e lo apre
   * in una nuova scheda - stesso schema di file-viewer.component.ts
   * (URL.createObjectURL, mai un <a href> diretto all'URL S3).
   *
   * Verificato dal vivo (click reale sul bottone "Visualizza"): un window.open()
   * chiamato DOPO che la risposta HTTP e' arrivata perde l'"activation" del click
   * originale che lo ha scatenato, e il browser lo blocca silenziosamente come
   * popup - nessun errore, il bottone "non fa nulla". Si apre subito una scheda
   * VUOTA, in modo sincrono dentro il gestore del click (questo preserva
   * l'activation), e la si reindirizza al blob una volta arrivato - stesso
   * workaround standard per "scarica poi apri" via richiesta autenticata.
   */
  public openCandidateDocument(
    candidateId: string | number,
    candidateDocumentId: number,
    which: 'source' | 'return',
    success_callback: () => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const newTab = window.open('', '_blank');

    this.viewCandidateDocument(
      candidateId,
      candidateDocumentId,
      which,
      (result: any) => {
        const url = URL.createObjectURL(result.body);
        if (newTab) {
          newTab.location.href = url;
        } else {
          // Popup comunque bloccato (o consenso popup gia' negato dall'utente per
          // questo sito) - ripiego sull'apertura diretta: puo' essere bloccata a
          // sua volta, ma e' comunque meglio di un silenzioso "non succede nulla".
          window.open(url, '_blank');
        }
        success_callback();
      },
      (error) => {
        newTab?.close();
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-402: il gestore verifica/respinge un file restituito dal clinico
   * (VERIFIED/ANOMALY, generalizzato dal vecchio "firma" SIGNED).
   */
  public updateCandidateDocumentStatus(
    surveyId: string | number,
    candidateDocumentId: number,
    status: string,
    anomalyReason: string = null,
    success_callback: (data: ClinicianCandidateDocument) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend
      .post(`surveys/${surveyId}/clinical-onboarding/candidate-documents/${candidateDocumentId}/status`, {
        status,
        anomaly_reason: anomalyReason,
      })
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /**
   * DBEK-411: il gestore invalida un documento gia' visionato, restituito o
   * verificato - lo riporta a PENDING (nessuna motivazione richiesta, a
   * differenza di ANOMALY), il clinico deve rifare l'azione.
   */
  public invalidateCandidateDocument(
    surveyId: string | number,
    candidateDocumentId: number,
    success_callback: (data: ClinicianCandidateDocument) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend
      .post(`surveys/${surveyId}/clinical-onboarding/candidate-documents/${candidateDocumentId}/invalidate`, {})
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /** Il gestore carica il file per-candidato su un template PER_CANDIDATE (es. la lettera di incarico personalizzata di un clinico). */
  public uploadCandidateDocumentFile(
    surveyId: string | number,
    candidateDocumentId: number,
    file: File,
    success_callback: (data: ClinicianCandidateDocument) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const formData = new FormData();
    formData.append('file', file);

    this.backend
      .postFormData(`surveys/${surveyId}/clinical-onboarding/candidate-documents/${candidateDocumentId}/file`, formData)
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /** DBEK-407: dashboard KPI + Tabella-1, filtrabile lato server per macro_status. */
  public getDashboard(
    surveyId: string | number,
    macroStatusFilter: string = null,
    success_callback: (data: ClinicianDashboardResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    let params = new HttpParams();
    if (macroStatusFilter) {
      params = params.set('macro_status', macroStatusFilter);
    }

    this.backend.get(`surveys/${surveyId}/clinical-onboarding/dashboard`, params).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** DBEK-392: il gestore abilita/configura l'onboarding clinico per la survey. */
  public updateConfig(
    surveyId: string | number,
    config: ClinicalOnboardingConfig,
    success_callback: (data: any) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/config`, config).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-412: il gestore allega un documento riservato a QUESTO candidato,
   * direttamente dalla sua scheda - non un template di survey, non comparirà mai
   * tra i "Documenti condivisi". Nessun file qui: si carica poi con lo stesso
   * controllo "Carica file" gia' in uso per i documenti Per-singolo-clinico
   * (uploadCandidateDocumentFile), riusando la riga appena creata.
   */
  public createCandidateOwnedDocument(
    surveyId: string | number,
    candidateId: number,
    payload: { title: string; requires_upload: boolean },
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend
      .post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/documents`, payload)
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /** Elenco dei template documento attivi per la survey, in ordine di visualizzazione. */
  public getDocumentTemplates(
    surveyId: string | number,
    success_callback: (data: ClinicianDocumentTemplate[]) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/clinical-onboarding/document-templates`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Feedback utente: "nei documenti condivisi dell'admin [...] non e' possibile
   * visualizzare il file caricato" - solo per template scope=GENERIC (l'unico che
   * ha un file proprio). Stesso pattern popup-safe di openCandidateDocument: una
   * scheda vuota aperta in modo sincrono nel gestore del click, reindirizzata al
   * blob quando arriva.
   */
  public openDocumentTemplateFile(
    surveyId: string | number,
    templateId: number,
    error_callback: (error: any) => void = () => {},
  ) {
    const newTab = window.open('', '_blank');

    this.backend.getBlob(`surveys/${surveyId}/clinical-onboarding/document-templates/${templateId}/file`).subscribe(
      (result: any) => {
        const url = URL.createObjectURL(result.body);
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.open(url, '_blank');
        }
      },
      (error) => {
        newTab?.close();
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Ridisegno "documenti flessibili" (sostituisce uploadDocumentTemplate): il
   * gestore definisce un documento con titolo libero + due assi indipendenti
   * (ambito, tipo), illimitati - niente piu' slot fisso per tipo. file solo per
   * scope=GENERIC (un template PER_CANDIDATE non ne ha uno proprio).
   */
  public createDocumentTemplate(
    surveyId: string | number,
    payload: {
      title: string;
      scope: string;
      requires_upload: boolean;
      visible_only_if_employer_public?: boolean;
      counts_as_invoice?: boolean;
      file?: File | null;
    },
    success_callback: (data: ClinicianDocumentTemplate) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('scope', payload.scope);
    formData.append('requires_upload', payload.requires_upload ? '1' : '0');
    formData.append('visible_only_if_employer_public', payload.visible_only_if_employer_public ? '1' : '0');
    formData.append('counts_as_invoice', payload.counts_as_invoice ? '1' : '0');
    if (payload.file) {
      formData.append('file', payload.file);
    }

    this.backend.postFormData(`surveys/${surveyId}/clinical-onboarding/document-templates`, formData).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Modifica titolo/flag/ordinamento di un template, e (solo GENERIC) sostituzione file in-place. */
  public updateDocumentTemplate(
    surveyId: string | number,
    templateId: number,
    payload: {
      title?: string;
      requires_upload?: boolean;
      visible_only_if_employer_public?: boolean;
      counts_as_invoice?: boolean;
      sort_order?: number;
      file?: File | null;
    },
    success_callback: (data: ClinicianDocumentTemplate) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    const formData = new FormData();
    // PUT con multipart non e' supportato in modo affidabile da tutti i client HTTP -
    // stesso trucco del metodo POST-con-override gia' comune in Laravel.
    formData.append('_method', 'PUT');
    if (payload.title !== undefined) {
      formData.append('title', payload.title);
    }
    if (payload.requires_upload !== undefined) {
      formData.append('requires_upload', payload.requires_upload ? '1' : '0');
    }
    if (payload.visible_only_if_employer_public !== undefined) {
      formData.append('visible_only_if_employer_public', payload.visible_only_if_employer_public ? '1' : '0');
    }
    if (payload.counts_as_invoice !== undefined) {
      formData.append('counts_as_invoice', payload.counts_as_invoice ? '1' : '0');
    }
    if (payload.sort_order !== undefined) {
      formData.append('sort_order', String(payload.sort_order));
    }
    if (payload.file) {
      formData.append('file', payload.file);
    }

    this.backend.postFormData(`surveys/${surveyId}/clinical-onboarding/document-templates/${templateId}`, formData).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Ritira un template (is_active=false) - le righe candidato gia' esistenti restano in storico. */
  public removeDocumentTemplate(
    surveyId: string | number,
    templateId: number,
    success_callback: (data: any) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.destroy(`surveys/${surveyId}/clinical-onboarding/document-templates/${templateId}`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-392: dettaglio di un singolo clinico (engagement + documenti), lato gestore.
   * candidateId, non teamMemberId - il dettaglio parte sempre dal Candidate (vedi
   * ClinicalOnboardingController::getClinicianDetail), non dal vero team member
   * eventualmente gia' importato.
   */
  public getClinicianDetail(
    surveyId: string | number,
    candidateId: number,
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-413: il gestore modifica tutti i campi dati di un clinico (anagrafica +
   * dati contrattuali) - finora il pannello di dettaglio era interamente in sola
   * lettura.
   *
   * Ridisegno UI in tab (Dettagli/Dati contrattuali/Documenti/Storico): il tab
   * "Dettagli" invia solo name/last_name/email, senza i campi contrattuali -
   * l'endpoint accetta invii parziali (employer_type reso opzionale lato server),
   * name/email restano sempre obbligatori in ogni chiamata.
   */
  public updateClinicianData(
    surveyId: string | number,
    candidateId: number,
    payload: {
      name: string;
      last_name?: string;
      email: string;
      title?: string;
      employer_name?: string;
      employer_type?: string;
      iban?: string;
      tax_code?: string;
      fiscal_regime?: string;
      gross_amount?: number;
      net_amount?: number;
      pa_authorization_status?: string;
      // Feedback utente: override permanente e prioritario del gestore sul
      // semaforo "Verifica" - true/false lo impostano, null lo rimuove (torna
      // alla verifica automatica). Assente = non toccare l'override esistente.
      verification_override?: boolean | null;
    },
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.put(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}`, payload).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Storico attivita' completo del candidato (feedback utente: "tracking
   * professionale e completo... quando accede alla documentazione, prende visione
   * di un file ecc"), raggiungibile dal pannello di dettaglio.
   */
  public getClinicianActivity(
    surveyId: string | number,
    candidateId: number,
    success_callback: (data: { events: ClinicianActivityEvent[] }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/activity`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Il gestore registra il pagamento di un clinico (poi modificabile o annullabile).
   * Senza paid_amount il backend usa il compenso netto del clinico.
   */
  public registerClinicianPayment(
    surveyId: string | number,
    candidateId: number,
    payload: { paid_amount?: number | null; payment_date?: string | null; cro_reference?: string | null },
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/payment`, payload).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Modifica importo / data / CRO di un pagamento gia' confermato. */
  public updateClinicianPayment(
    surveyId: string | number,
    candidateId: number,
    payload: { paid_amount?: number | null; payment_date?: string; cro_reference?: string | null },
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.put(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/payment`, payload).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Annulla un pagamento confermato: torna "da pagare", i suoi dati vengono azzerati. */
  public cancelClinicianPayment(
    surveyId: string | number,
    candidateId: number,
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.destroy(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/payment`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Esclude (excluded=true) o riammette (excluded=false) un clinico dal processo. */
  public setClinicianExclusion(
    surveyId: string | number,
    candidateId: number,
    excluded: boolean,
    reason: string | null,
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/exclusion`, { excluded, reason }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Note interne del gestore su un candidato (piu' recenti prima). */
  public getClinicianNotes(
    surveyId: string | number,
    candidateId: number,
    success_callback: (data: { notes: ClinicianNote[] }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.get(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/notes`).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  public addClinicianNote(
    surveyId: string | number,
    candidateId: number,
    body: string,
    success_callback: (note: ClinicianNote) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/notes`, { body }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  public updateClinicianNote(
    surveyId: string | number,
    candidateId: number,
    noteId: number,
    body: string,
    success_callback: (note: ClinicianNote) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.put(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/notes/${noteId}`, { body }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  public removeClinicianNote(
    surveyId: string | number,
    candidateId: number,
    noteId: number,
    success_callback: () => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.destroy(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/notes/${noteId}`).subscribe(
      () => success_callback(),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * Visualizzazione esplicita in chiaro di IBAN o codice fiscale (cifrati a riposo):
   * un campo per richiesta, ogni richiesta e' registrata nello storico attivita'.
   */
  public revealClinicianSensitiveField(
    surveyId: string | number,
    candidateId: number,
    field: 'iban' | 'tax_code',
    success_callback: (data: { field: string; value: string }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/reveal-sensitive-field`, { field }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /** Forza (enabled=true) o rimuove la partecipazione manuale del clinico. */
  public setClinicianParticipationOverride(
    surveyId: string | number,
    candidateId: number,
    enabled: boolean,
    reason: string | null,
    success_callback: (data: ClinicianDetailResponse) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/clinicians/${candidateId}/participation-override`, { enabled, reason }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-395 (ridisegno "candidato"): il gestore registra uno o piu' futuri clinici
   * PRIMA che la survey sia partita (piano §"c'e' la possibilita' di far partire le
   * lettere d'incarico e registrare i futuri medici prima che la survey sia
   * effettivamente partita") - upsert per (survey_history_id, email) lato backend,
   * quindi ri-registrare la stessa email aggiorna solo nome/cognome invece di
   * duplicare (vedi ClinicalOnboardingController::registerCandidates()).
   */
  public registerCandidates(
    surveyId: string | number,
    candidates: { name: string; last_name?: string; email: string }[],
    success_callback: (data: { added: number; updated: number }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/candidates`, { candidates }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }

  /**
   * DBEK-395: importazione MANUALE dei candidati pronti nel team reale della survey,
   * disponibile in qualsiasi momento (anche a survey ancora in bozza) in aggiunta al
   * trigger automatico che scatta quando la survey esce dalla bozza (vedi
   * OrganizationSurvey::booted() lato backend). candidateIds omesso o vuoto importa
   * tutti i candidati "pronti" (macro_status SIGNED/INVOICE_RECEIVED/PAID) non
   * ancora importati.
   */
  public importCandidates(
    surveyId: string | number,
    candidateIds: number[] | null,
    success_callback: (data: { imported: number }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend
      .post(`surveys/${surveyId}/clinical-onboarding/import-candidates`, { candidate_ids: candidateIds || undefined })
      .subscribe(
        (result: any) => success_callback(result),
        (error) => {
          this.backend.showErrors(error);
          error_callback(error);
        },
      );
  }

  /**
   * Sollecito MANUALE (singolo candidato o selezione multipla), in aggiunta al
   * reminder automatico giorno-N-da-X gia' esistente. Solo i candidati
   * WAITING/IN_PROGRESS sono ammessi lato backend (vedi
   * ClinicalOnboardingController::remindCandidates) - la UI filtra la
   * selezionabilita' delle righe allo stesso modo.
   */
  public remindCandidates(
    surveyId: string | number,
    candidateIds: number[],
    success_callback: (data: { sent: number }) => void = () => {},
    error_callback: (error: any) => void = () => {},
  ) {
    this.backend.post(`surveys/${surveyId}/clinical-onboarding/remind-candidates`, { candidate_ids: candidateIds }).subscribe(
      (result: any) => success_callback(result),
      (error) => {
        this.backend.showErrors(error);
        error_callback(error);
      },
    );
  }
}
